import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Card,
  Table,
  Button,
  Form,
  Modal,
  message,
  Space,
  Popconfirm,
  Input,
  Tag,
  Collapse,
  Empty,
  Tooltip,
  Spin,
  Typography,
  Divider,
  Radio,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ApiOutlined,
  SyncOutlined,
  EyeInvisibleOutlined,
  SafetyCertificateOutlined,
  LinkOutlined,
  MailOutlined,
  CreditCardOutlined,
} from "@ant-design/icons";
import useGlobalFilters from "../../../../hooks/useGlobalFilters";
import {
  fetchGatewayCredentials,
  createGatewayCredential,
  updateGatewayCredential,
  deleteGatewayCredential,
  fetchWordPressCredentials,
  createWordPressCredential,
  updateWordPressCredential,
  deleteWordPressCredential,
  fetchStripeGatewayCredentials,
  createStripeGatewayCredential,
  updateStripeGatewayCredential,
  deleteStripeGatewayCredential,
  fetchSublyticsCredentials,
  createSublyticsCredential,
  updateSublyticsCredential,
  deleteSublyticsCredential,
  fetchNMIGatewayCredentials,
  createNMIGatewayCredential,
  updateNMIGatewayCredential,
  deleteNMIGatewayCredential,
  fetchShipStationCredentials,
  createShipStationCredential,
  updateShipStationCredential,
  deleteShipStationCredential,
  fetchTigerTechSFTPCredentials,
  createTigerTechSFTPCredential,
  updateTigerTechSFTPCredential,
  deleteTigerTechSFTPCredential,
  fetchReliableGatewayCredentials,
  createReliableGatewayCredential,
  updateReliableGatewayCredential,
  deleteReliableGatewayCredential,
  fetchPasRadeCredentials,
  createPasRadeCredential,
  updatePasRadeCredential,
  deletePasRadeCredential,
  fetchElavonCredentials,
  createElavonCredential,
  updateElavonCredential,
  deleteElavonCredential,
} from "../../../../core/api/data";

const { Text, Title } = Typography;
const { Panel } = Collapse;

// Platform configuration - defines the form fields and API mapping for each platform
const PLATFORM_CONFIG = {
  authnet: {
    key: "authnet",
    label: "Authorize.NET",
    icon: <SafetyCertificateOutlined />,
    color: "#fa8c16",
    description: "Payment gateway for processing credit card transactions",
    fetchFn: fetchGatewayCredentials,
    createFn: createGatewayCredential,
    updateFn: updateGatewayCredential,
    deleteFn: deleteGatewayCredential,
    multiple: true,
    fields: [
      { name: "account_name", label: "Account Name", required: true, secret: false },
      { name: "login_id", label: "API Login ID", required: true, secret: true },
      { name: "transaction_key", label: "Transaction Key", required: true, secret: true },
      { name: "api_url", label: "API URL", required: true, secret: true, placeholder: "https://api.authorize.net/xml/v1/request.api" },
    ],
    statusField: "has_credentials",
    displayFields: ["account_name"],
    lastModifiedField: "last_modified",
  },
  wordpress: {
    key: "wordpress",
    label: "WordPress",
    icon: <LinkOutlined />,
    color: "#52c41a",
    description: "WooCommerce REST API credentials for order syncing",
    fetchFn: fetchWordPressCredentials,
    createFn: createWordPressCredential,
    updateFn: updateWordPressCredential,
    deleteFn: deleteWordPressCredential,
    multiple: false,
    fields: [
      { name: "website", label: "Website URL", required: true, secret: false, placeholder: "https://yoursite.com" },
      { name: "consumer_key", label: "Consumer Key", required: true, secret: true },
      { name: "consumer_secret", label: "Consumer Secret", required: true, secret: true },
    ],
    statusField: "has_credentials",
    displayFields: ["website"],
    lastModifiedField: "last_modified",
  },
  stripe: {
    key: "stripe",
    label: "Stripe",
    icon: <SafetyCertificateOutlined />,
    color: "#722ed1",
    description: "Stripe payment gateway for transaction data",
    fetchFn: fetchStripeGatewayCredentials,
    createFn: createStripeGatewayCredential,
    updateFn: updateStripeGatewayCredential,
    deleteFn: deleteStripeGatewayCredential,
    multiple: true,
    fields: [
      { name: "account_name", label: "Account Name", required: true, secret: false },
      { name: "secret_key", label: "Secret Key", required: true, secret: true, placeholder: "sk_live_..." },
    ],
    statusField: "has_credentials",
    displayFields: ["account_name"],
    lastModifiedField: "last_modified",
  },
  sublytics: {
    key: "sublytics",
    label: "Sublytics",
    icon: <ApiOutlined />,
    color: "#1890ff",
    description: "CRM platform for order management and reporting",
    fetchFn: fetchSublyticsCredentials,
    createFn: createSublyticsCredential,
    updateFn: updateSublyticsCredential,
    deleteFn: deleteSublyticsCredential,
    multiple: true,
    fields: [
      { name: "api_id", label: "API ID", required: true, secret: true },
      { name: "api_password", label: "API Password", required: true, secret: true },
    ],
    statusField: "has_credentials",
    displayFields: [],
    lastModifiedField: "last_modified",
  },
  nmi: {
    key: "nmi",
    label: "NMI Payarc",
    icon: <SafetyCertificateOutlined />,
    color: "#eb2f96",
    description: "NMI payment gateway for transaction processing",
    fetchFn: fetchNMIGatewayCredentials,
    createFn: createNMIGatewayCredential,
    updateFn: updateNMIGatewayCredential,
    deleteFn: deleteNMIGatewayCredential,
    multiple: true,
    fields: [
      { name: "account_name", label: "Account Name", required: true, secret: false },
      { name: "secret_key", label: "Security Key", required: true, secret: true },
    ],
    statusField: "has_credentials",
    displayFields: ["account_name"],
    lastModifiedField: "last_modified_at",
  },
  tigertech: {
    key: "tigertech",
    label: "TigerTech SFTP",
    icon: <SyncOutlined />,
    color: "#13c2c2",
    description: "SFTP connection for TigerTech sales file imports",
    fetchFn: fetchTigerTechSFTPCredentials,
    createFn: createTigerTechSFTPCredential,
    updateFn: updateTigerTechSFTPCredential,
    deleteFn: deleteTigerTechSFTPCredential,
    multiple: true,
    fields: [
      { name: "host", label: "SFTP Host", required: true, secret: false },
      { name: "port", label: "Port", required: false, secret: false, placeholder: "22", inputType: "number" },
      { name: "username", label: "Username", required: true, secret: false },
      { name: "password", label: "Password", required: true, secret: true },
      { name: "upload_directory", label: "Upload Directory", required: false, secret: false, placeholder: "/upload/" },
      { name: "source", label: "Source", required: false, secret: false, placeholder: "TigerTech" },
    ],
    statusField: null,
    displayFields: ["host", "username"],
    lastModifiedField: "updated_at",
  },
  shipstation: {
    key: "shipstation",
    label: "ShipStation",
    icon: <ApiOutlined />,
    color: "#2f54eb",
    description: "ShipStation API credentials for inventory and fulfillment data",
    fetchFn: fetchShipStationCredentials,
    createFn: createShipStationCredential,
    updateFn: updateShipStationCredential,
    deleteFn: deleteShipStationCredential,
    multiple: false,
    fields: [
      { name: "v2_api_key", label: "API Key (v2)", required: true, secret: true, placeholder: "Generate in ShipStation → Settings → API" },
      { name: "api_key", label: "API Key", required: false, secret: true },
      { name: "api_secret", label: "Secret Key", required: false, secret: true },
    ],
    statusField: "has_credentials",
    displayFields: ["store_id"],
    lastModifiedField: "updated_at",
  },
  reliable: {
    key: "reliable",
    label: "Reliable (Check Gateway)",
    icon: <SafetyCertificateOutlined />,
    color: "#8c8c8c",
    description: "Reliable Solutions gateway — Bearer token auth (reliabledashboard.com/api)",
    fetchFn: fetchReliableGatewayCredentials,
    createFn: createReliableGatewayCredential,
    updateFn: updateReliableGatewayCredential,
    deleteFn: deleteReliableGatewayCredential,
    multiple: true,
    fields: [
      { name: "account_name", label: "Account Name", required: false, secret: false, placeholder: "Display label for this credential" },
      { name: "api_key", label: "API Access Token", required: true, secret: true, placeholder: "Bearer access token from Reliable dashboard" },
    ],
    statusField: "has_credentials",
    displayFields: ["account_name"],
    lastModifiedField: "updated_at",
  },
  pasrade: {
    key: "pasrade",
    label: "PAS RADE",
    icon: <MailOutlined />,
    color: "#eb2f96",
    description: "PAS RADE report scraper — automated mailing/LTV & sales dump downloads from paslists.com",
    fetchFn: fetchPasRadeCredentials,
    createFn: createPasRadeCredential,
    updateFn: updatePasRadeCredential,
    deleteFn: deletePasRadeCredential,
    multiple: false,
    fields: [
      { name: "url", label: "URL", required: true, secret: false, placeholder: "https://www.paslists.com/rade-nal/" },
      { name: "username", label: "Username", required: true, secret: true },
      { name: "password", label: "Password", required: true, secret: true },
    ],
    statusField: "has_credentials",
    displayFields: ["url"],
    lastModifiedField: "updated_at",
  },
  elavon: {
    key: "elavon",
    label: "Elavon",
    icon: <CreditCardOutlined />,
    color: "#1d4ed8",
    description:
      "Same mypaymentsinsider login for Max Vital (MID 8043995540) and House Of Man (8045441501). Enter it once; choose whether to store both MIDs or only this brand’s MID.",
    fetchFn: fetchElavonCredentials,
    createFn: createElavonCredential,
    updateFn: updateElavonCredential,
    deleteFn: deleteElavonCredential,
    global: false,
    multiple: false,
    fields: [
      { name: "username", label: "Login / Username", required: true, secret: true },
      { name: "password", label: "Password", required: true, secret: true },
    ],
    statusField: "has_credentials",
    displayFields: ["brand_name"],
    lastModifiedField: "updated_at",
  },
};

const PLATFORMS = Object.values(PLATFORM_CONFIG);

/** Elavon only applies to these two brands (shared portal MIDs). */
function isElavonBrand(brand) {
  const n = (brand?.brand_name || "").trim().toLowerCase();
  return n === "max vital" || n === "house of man";
}

/**
 * A single platform credential section - handles CRUD for one platform type.
 */
const PlatformCredentialSection = ({ platform, brandId, brandName }) => {
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const isGlobal = !!platform.global;

  const fetchData = useCallback(async () => {
    if (!isGlobal && !brandId) return;
    setLoading(true);
    try {
      const res = isGlobal ? await platform.fetchFn() : await platform.fetchFn(brandId);
      const raw = res.results ?? res ?? [];
      const data = Array.isArray(raw)
        ? raw
        : raw && typeof raw === "object" && "id" in raw
          ? [raw]
          : [];
      setCredentials(data);
    } catch (error) {
      console.warn(`Failed to fetch ${platform.label} credentials:`, error);
      setCredentials([]);
    } finally {
      setLoading(false);
    }
  }, [brandId, platform, isGlobal]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    if (!isGlobal) form.setFieldsValue({ brand: brandId });
    if (platform.key === "elavon") {
      form.setFieldsValue({ elavon_store_scope: "both" });
    }
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    form.resetFields();
    const values = isGlobal ? {} : { brand: brandId };
    platform.fields.forEach((f) => {
      if (!f.secret && record[f.name] !== undefined) {
        values[f.name] = record[f.name];
      }
    });
    if (platform.key === "elavon") {
      values.elavon_store_scope = record.brand == null ? "both" : "this_brand";
    }
    form.setFieldsValue(values);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await platform.deleteFn(id);
      message.success(`${platform.label} credential deleted`);
      fetchData();
    } catch (error) {
      console.error("Delete error:", error);
      message.error(`Failed to delete ${platform.label} credential`);
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      let payload = isGlobal ? { ...values } : { ...values, brand: brandId };
      if (platform.key === "elavon") {
        const scope = values.elavon_store_scope;
        payload.brand = scope === "both" ? null : brandId;
        delete payload.elavon_store_scope;
      }
      // Strip empty string values for secret fields (don't overwrite with blank)
      platform.fields.forEach((f) => {
        if (f.secret && (!payload[f.name] || payload[f.name].trim() === "")) {
          delete payload[f.name];
        }
      });

      if (editingRecord) {
        await platform.updateFn(editingRecord.id, payload);
        message.success(`${platform.label} credential updated`);
      } else {
        await platform.createFn(payload);
        message.success(`${platform.label} credential created`);
      }
      setModalVisible(false);
      fetchData();
    } catch (error) {
      if (error.errorFields) return; // form validation error
      console.error("Save error:", error);
      const detail = error.response?.data;
      if (detail && typeof detail === "object") {
        const msgs = Object.entries(detail)
          .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
          .join("; ");
        message.error(msgs || `Failed to save ${platform.label} credential`);
      } else {
        message.error(`Failed to save ${platform.label} credential`);
      }
    } finally {
      setSaving(false);
    }
  };

  // Build table columns dynamically
  const columns = [
    ...platform.displayFields.map((fieldName) => {
      const fieldConfig = platform.fields.find((f) => f.name === fieldName);
      return {
        title: fieldConfig?.label || fieldName,
        dataIndex: fieldName,
        key: fieldName,
        ellipsis: true,
      };
    }),
    ...(platform.statusField
      ? [
          {
            title: "Status",
            key: "status",
            width: 120,
            render: (_, record) => {
              const connected = record[platform.statusField];
              const expired = platform.extraStatusField ? record[platform.extraStatusField] : false;
              if (expired) {
                return <Tag color="orange">Expired</Tag>;
              }
              return connected ? (
                <Tag icon={<CheckCircleOutlined />} color="success">Connected</Tag>
              ) : (
                <Tag icon={<CloseCircleOutlined />} color="error">Missing Keys</Tag>
              );
            },
          },
        ]
      : []),
    ...(platform.lastModifiedField
      ? [
          {
            title: "Last Modified",
            dataIndex: platform.lastModifiedField,
            key: "lastModified",
            width: 180,
            render: (val) => (val ? new Date(val).toLocaleString() : "N/A"),
          },
        ]
      : []),
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title={`Delete this ${platform.label} credential?`}
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete">
              <Button type="link" size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // If no display fields, add a generic ID column
  if (platform.displayFields.length === 0) {
    columns.unshift({
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
    });
  }

  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <Space>
          <Tag color={platform.color} style={{ fontSize: 13, padding: "2px 10px" }}>
            {platform.icon} {platform.label}
          </Tag>
          <Text type="secondary" style={{ fontSize: 12 }}>{platform.description}</Text>
        </Space>
        {(platform.multiple || credentials.length === 0) && (
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            Add {platform.label}
          </Button>
        )}
      </div>

      <Table
        columns={columns}
        dataSource={credentials}
        rowKey="id"
        size="small"
        loading={loading}
        pagination={false}
        locale={{ emptyText: <Empty description={`No ${platform.label} credentials configured`} image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
      />

      {/* Add/Edit Modal */}
      <Modal
        title={`${editingRecord ? "Edit" : "Add"} ${platform.label} Credentials`}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleSave}
        confirmLoading={saving}
        destroyOnClose
        width={520}
      >
        <Form form={form} layout="vertical" preserve={false}>
          {!isGlobal && (
            <Form.Item name="brand" hidden>
              <Input />
            </Form.Item>
          )}

          {platform.fields.map((field) => (
            <Form.Item
              key={field.name}
              name={field.name}
              label={
                <Space>
                  {field.label}
                  {field.secret && (
                    <Tooltip title="This value is encrypted and stored securely. It will not be displayed after saving.">
                      <EyeInvisibleOutlined style={{ color: "#999" }} />
                    </Tooltip>
                  )}
                </Space>
              }
              rules={[
                {
                  required: editingRecord ? false : field.required,
                  message: `Please enter ${field.label}`,
                },
              ]}
              extra={editingRecord && field.secret ? "Leave blank to keep existing value" : undefined}
            >
              {field.secret ? (
                <Input.Password
                  placeholder={editingRecord ? "••••••••  (leave blank to keep)" : field.placeholder || `Enter ${field.label}`}
                  autoComplete="off"
                />
              ) : field.inputType === "number" ? (
                <Input
                  type="number"
                  placeholder={field.placeholder || `Enter ${field.label}`}
                />
              ) : (
                <Input placeholder={field.placeholder || `Enter ${field.label}`} />
              )}
            </Form.Item>
          ))}
          {platform.key === "elavon" && (
            <Form.Item
              name="elavon_store_scope"
              label="Which MIDs to store"
              initialValue="both"
              extra="Same portal login for both merchants. We can save rows for both MIDs, or only the MID for the brand shown on this panel. The download may still list all locations; rows outside your choice are not stored."
            >
              <Radio.Group>
                <Radio value="both">Both — Max Vital + House Of Man</Radio>
                <Radio value="this_brand">{brandName} only</Radio>
              </Radio.Group>
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

/**
 * Main Integrations Tab - shows all platform credentials for the selected brand.
 */
const IntegrationsTab = ({ selectedBrand = null }) => {
  const { selectedBrands } = useGlobalFilters();

  const brandsToUse = useMemo(() => {
    return selectedBrands && selectedBrands.length > 0
      ? selectedBrands
      : selectedBrand
      ? [selectedBrand]
      : [];
  }, [selectedBrands, selectedBrand]);

  if (brandsToUse.length === 0) {
    return (
      <Empty description="Select a client/brand from the global filter to manage platform integrations" />
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Text type="secondary">
          Manage API keys and credentials for each platform integration. Credentials are encrypted and stored securely.
          Secret values (keys, passwords, tokens) are write-only and will not be displayed after saving.
        </Text>
      </div>

      <Collapse
        defaultActiveKey={brandsToUse.length === 1 ? [String(brandsToUse[0].id)] : []}
        accordion={false}
      >
        {brandsToUse.map((brand) => (
          <Panel
            key={String(brand.id)}
            header={
              <Space>
                <Tag color="blue" style={{ fontSize: 14, padding: "2px 12px" }}>
                  {brand.brand_name}
                </Tag>
                <Text type="secondary">Platform Credentials</Text>
              </Space>
            }
          >
            {PLATFORMS.filter((p) => (isElavonBrand(brand) ? true : p.key !== "elavon")).map((platform) => (
              <React.Fragment key={platform.key}>
                <PlatformCredentialSection
                  platform={platform}
                  brandId={brand.id}
                  brandName={brand.brand_name}
                />
                <Divider style={{ margin: "12px 0" }} />
              </React.Fragment>
            ))}
          </Panel>
        ))}
      </Collapse>
    </div>
  );
};

export default IntegrationsTab;
